import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { backend } from '@/api/backend';
import { explainLine } from '@/api/explain';
import { ApiError } from '@/api/http';
import { Panel, PanelHeader, Skeleton, Stamp, ReadFailed } from '@/components/ui';
import { formatBytes, formatCount } from '@/lib/format';
import { cn } from '@/lib/cn';
import { say } from '@/lib/vocabulary';

/**
 * The archives, and whether what is inside them can actually be read.
 *
 * Most of a corpus's weight is often locked inside zips and tarballs, and a
 * corpus is only worth what can be opened. The walk enumerates every archive
 * it finds and records how it fared, and none of that reached the product: a
 * bucket could be 95 GiB of readable content or 95 GiB of encrypted noise and
 * the screen looked identical.
 *
 * Integrity leads, because it is the one fact that changes the answer. An
 * archive that is corrupt, truncated or password-protected is called out
 * rather than counted quietly alongside the ones that opened cleanly.
 */

const INTEGRITY: Record<string, { label: string; tone: 'ok' | 'warn' | 'bad' | 'mut' }> = {
  VALID: { label: 'opened cleanly', tone: 'ok' },
  VALID_WITH_WARNINGS: { label: 'opened, with warnings', tone: 'warn' },
  NOT_TESTED: { label: 'not opened yet', tone: 'mut' },
  CORRUPT: { label: 'corrupt', tone: 'bad' },
  TRUNCATED: { label: 'cut short', tone: 'bad' },
  ENCRYPTED: { label: 'encrypted', tone: 'warn' },
  PASSWORD_REQUIRED: { label: 'needs a password', tone: 'warn' },
  UNSUPPORTED: { label: 'format not supported', tone: 'warn' },
  // The ninth state in the contract, absent from this map, so it fell
  // through every guard and landed on "this archive opened and holds no
  // members", which is the one sentence this screen exists to avoid.
  INDETERMINATE: { label: 'could not be decided', tone: 'warn' },
};

interface Archive {
  archiveId: string;
  objectKey: string;
  formatSlug: string;
  integrityState: string;
  /**
   * Null where nothing has counted. The wire models every one of these as
   * nullable, and coercing them to zero here printed "0 members · 0 B" for an
   * archive nobody had opened, which is the one sentence this screen exists to
   * avoid. formatBytes and formatCount already render null as an em dash.
   */
  memberCount: number | null;
  compressedBytes: number | null;
  expandedBytes: number | null;
  maxDepth: number | null;
}

function read(value: unknown): Archive[] | null {
  const data = (value as { data?: unknown })?.data ?? value;
  const raw = (data as Record<string, unknown> | null)?.archives;
  if (!Array.isArray(raw)) return null;
  const num = (v: unknown) => (typeof v === 'number' ? v : null);
  return (raw as Record<string, unknown>[]).map((a) => ({
    archiveId: String(a.archiveId ?? ''),
    objectKey: String(a.objectKey ?? ''),
    formatSlug: String(a.formatSlug ?? ''),
    integrityState: String(a.integrityState ?? 'NOT_TESTED'),
    memberCount: num(a.memberCount),
    compressedBytes: num(a.compressedBytes),
    expandedBytes: num(a.expandedBytes),
    maxDepth: num(a.maxDepth),
  }));
}

/**
 * Why an archive has no member list to fetch, or null when it should have one.
 *
 * An archive that was never opened has no members recorded, and asking for
 * them returns an empty list that reads as "this zip is empty". That is the
 * opposite of what happened: nothing looked inside it. The integrity state is
 * already on the row, so the answer is known before the request is made.
 */
function noMembersBecause(integrityState: string, memberCount: number | null): string | null {
  // An archive that recorded members has them, whatever its integrity state.
  // Gating on the state alone suppressed the member list for ENCRYPTED and
  // PASSWORD_REQUIRED archives, which routinely publish a readable directory
  // of encrypted entries: the row would say "1,204 members" and the panel it
  // opened would say the contents were never enumerated.
  if (memberCount !== null && memberCount > 0) return null;
  if (integrityState === 'NOT_TESTED')
    return 'Nothing has looked inside this archive yet, so it has no members recorded. Its size and format are read from the object itself.';
  if (
    INTEGRITY[integrityState] &&
    ['CORRUPT', 'TRUNCATED', 'ENCRYPTED', 'PASSWORD_REQUIRED', 'UNSUPPORTED', 'INDETERMINATE'].includes(integrityState)
  )
    return `This archive ${INTEGRITY[integrityState]!.label}, so its contents were never enumerated. What is inside it has not been read.`;
  return null;
}

/**
 * One page of members, or an explicitly unrecognised shape.
 *
 * The panel-level guard was applied one level too high: inside the expansion
 * a body the client cannot walk still fell to an empty array and printed
 * "this archive opened and holds no members".
 */
function readMembers(value: unknown): {
  members: Record<string, unknown>[];
  nextPath: string | null;
  nextId: string | null;
} {
  const d = ((value as { data?: unknown })?.data ?? value) as Record<string, unknown> | null;
  const raw = d?.members;
  return {
    members: Array.isArray(raw) ? (raw as Record<string, unknown>[]) : [],
    nextPath: typeof d?.nextMemberPath === 'string' ? d.nextMemberPath : null,
    nextId: typeof d?.nextMemberId === 'string' ? d.nextMemberId : null,
  };
}

function Members({
  bucketId,
  archiveId,
  integrityState,
  memberCount,
}: {
  bucketId: string;
  archiveId: string;
  integrityState: string;
  memberCount: number | null;
}) {
  const blocked = noMembersBecause(integrityState, memberCount);
  /**
   * Members, page by page, with BOTH halves of the keyset cursor.
   *
   * The request sent neither half and the list then sliced to forty, so an
   * archive of a hundred members drew forty rows under "100 members, all
   * shown", and nothing past the first page was reachable at all. The
   * contract requires both `afterMemberPath` and `afterMemberId` together and
   * warns that half a cursor silently restarts while appearing to continue.
   */
  const q = useInfiniteQuery({
    queryKey: ['archive-members', bucketId, archiveId],
    initialPageParam: undefined as { path: string; id: string } | undefined,
    queryFn: ({ pageParam, signal }) =>
      backend.listArchiveMembers(
        bucketId,
        archiveId,
        { pageSize: 100, afterMemberPath: pageParam?.path, afterMemberId: pageParam?.id },
        signal,
      ),
    getNextPageParam: (last) => {
      const page = readMembers(last);
      return page.nextPath && page.nextId ? { path: page.nextPath, id: page.nextId } : undefined;
    },
    retry: (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2),
    staleTime: 300_000,
    enabled: blocked === null,
  });
  const members = (q.data?.pages ?? []).flatMap((p) => readMembers(p).members);

  if (blocked) return <p className="bg-panel-2 px-4 py-2 text-caption text-ink-2">{blocked}</p>;
  if (q.isPending) return <Skeleton className="mx-4 my-2 h-5" />;
  if (q.isError) {
    return <ReadFailed what={explainLine(q.error, 'read this archive')} onRetry={() => void q.refetch()} />;
  }
  return (
    <div className="bg-panel-2">
      {members.map((m) => (
        <div key={String(m.memberId)} className="flex flex-wrap items-baseline gap-x-3 border-b border-line px-4 py-1.5 last:border-0">
          <span className="mono min-w-0 flex-1 truncate text-caption text-ink" title={String(m.memberPath)}>
            {String(m.memberPath)}
          </span>
          {m.isEncrypted === true && <span className="text-caption text-warn">encrypted</span>}
          {m.pathIsSafe === false && <span className="text-caption text-warn">unsafe path</span>}
          {typeof m.nestedArchiveId === 'string' && m.nestedArchiveId && (
            <span className="text-caption text-acc-2">an archive itself</span>
          )}
          <span className="mono w-20 flex-none text-right text-caption text-ink-3">
            {formatBytes(typeof m.expandedBytes === 'number' ? m.expandedBytes : null)}
          </span>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-2 text-caption text-ink-3">
        <span className="min-w-0 flex-1">
          {members.length === 0
            ? 'This archive opened and holds no members of its own.'
            : q.hasNextPage
              ? `${formatCount(members.length)} read so far, and there are more.`
              : `${formatCount(members.length)} members, all of them.`}
        </span>
        {q.hasNextPage && (
          <button
            type="button"
            disabled={q.isFetchingNextPage}
            onClick={() => void q.fetchNextPage()}
            className="flex-none rounded border border-line bg-panel px-2.5 py-1 text-acc-2 transition-colors duration-fast hover:bg-hov disabled:opacity-50"
          >
            {q.isFetchingNextPage ? 'Reading…' : 'Read the next 100'}
          </button>
        )}
      </div>
    </div>
  );
}

export function Archives({ bucketId }: { bucketId: string }) {
  const q = useQuery({
    queryKey: ['archives', bucketId],
    queryFn: ({ signal }) => backend.listArchives(bucketId, signal),
    retry: (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2),
    staleTime: 120_000,
  });
  // One archive's expansion used to close another's, so two could not be
  // compared, and the id survived a change of bucket.
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setOpen((prior) => {
      const next = new Set(prior);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const archives = q.data ? read(q.data) : null;
  const sorted = [...(archives ?? [])].sort((a, b) => (b.expandedBytes ?? -1) - (a.expandedBytes ?? -1));
  // Sums run over what was measured, and the count of what was not travels
  // with them. A total that quietly absorbs unmeasured archives reads as the
  // whole corpus.
  const counted = sorted.filter((a) => a.memberCount !== null);
  const members = counted.reduce((n, a) => n + (a.memberCount ?? 0), 0);
  const expanded = sorted.reduce((n, a) => n + (a.expandedBytes ?? 0), 0);
  const unreadable = sorted.filter((a) => ['CORRUPT', 'TRUNCATED', 'ENCRYPTED', 'PASSWORD_REQUIRED', 'UNSUPPORTED'].includes(a.integrityState));
  // NOT_TESTED is neither opened nor unopenable: nothing tried. It was
  // excluded from both counts, so a bucket of entirely un-inspected archives
  // reported "every one opened".
  const untested = sorted.filter((a) => a.integrityState === 'NOT_TESTED' || a.integrityState === 'INDETERMINATE');

  // Two different nothings shared one blank screen. A bucket with no archives
  // in it is a finished answer, and the panel stays out of the way for it. A
  // body this build cannot read is a failure, and disappearing is how it got
  // reported as "no archives" on a bucket that has them.
  const unreadablePayload = !q.isPending && !q.isError && archives === null;
  if (!q.isPending && !q.isError && archives !== null && archives.length === 0) return null;

  return (
    <Panel>
      <PanelHeader
        title="What is inside the archives"
        sub={
          q.isPending
            ? 'reading every archive the walk opened'
            : `${sorted.length} archives · ${formatCount(members)} members${
                counted.length < sorted.length ? ` in ${counted.length} that were counted` : ''
              } · ${formatBytes(expanded)} of content${
                unreadable.length > 0 ? ` · ${unreadable.length} could not be opened` : ''
              }${untested.length > 0 ? ` · ${untested.length} not opened yet` : ''}`
        }
      />
      {q.isPending ? (
        <div className="space-y-2 p-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-full" />
          ))}
        </div>
      ) : q.isError ? (
        <ReadFailed
          what={q.error instanceof ApiError ? (q.error.reason ?? q.error.message) : 'The API did not respond.'}
          onRetry={() => void q.refetch()}
        />
      ) : unreadablePayload ? (
        <p className="px-4 py-3 text-small text-warn">
          The archive index came back in a shape this build does not recognise, so nothing here is drawn. This says
          nothing about whether the bucket holds archives.
        </p>
      ) : (
        <>
          {sorted.map((a) => {
            const state = INTEGRITY[a.integrityState] ?? { label: say(a.integrityState), tone: 'mut' as const };
            const isOpen = open.has(a.archiveId);
            const tail = a.objectKey.split('/').pop() || a.objectKey;
            return (
              <div key={a.archiveId} className="border-b border-line last:border-0">
                <button
                  type="button"
                  onClick={() => toggle(a.archiveId)}
                  aria-expanded={isOpen}
                  aria-controls={`archive-${a.archiveId}`}
                  className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left transition-colors duration-fast hover:bg-hov"
                >
                  <ChevronRight className={cn('h-3.5 w-3.5 flex-none text-ink-4 transition-transform', isOpen && 'rotate-90')} aria-hidden="true" />
                  <span className="mono min-w-0 flex-1 truncate text-small text-ink" title={a.objectKey}>
                    {tail}
                  </span>
                  <span className="text-caption text-ink-4">{a.formatSlug}</span>
                  <span className="mono w-28 flex-none text-right text-caption text-ink-2">
                    {formatCount(a.memberCount)} members
                  </span>
                  <span className="mono w-20 flex-none text-right text-caption text-ink-3">{formatBytes(a.expandedBytes)}</span>
                  <Stamp tone={state.tone}>{state.label}</Stamp>
                </button>
                {isOpen && (
                  <div id={`archive-${a.archiveId}`} role="region" aria-label={`Inside ${tail}`}>
                  <Members
                    bucketId={bucketId}
                    archiveId={a.archiveId}
                    integrityState={a.integrityState}
                    memberCount={a.memberCount}
                  />
                  </div>
                )}
              </div>
            );
          })}
          <p className="border-t border-line px-4 py-2.5 text-caption text-ink-3">
            {formatBytes(expanded)} of content sits inside{' '}
            {formatBytes(sorted.reduce((n, a) => n + (a.compressedBytes ?? 0), 0))} of archives.{' '}
            {unreadable.length > 0
              ? `${unreadable.length} could not be opened, and that content cannot be sold as readable.`
              : untested.length > 0
                ? `${untested.length} of these has not been opened yet, so what is inside ${untested.length === 1 ? 'it' : 'them'} is still unknown.`
                : 'Every one opened, so none of this corpus is locked away.'}
          </p>
        </>
      )}
    </Panel>
  );
}
